import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, MessageSquarePlus, RefreshCcw, Send, ShieldAlert, Sparkles } from "lucide-react";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/api/client";
import CitationRefs from "./CitationRefs";

/**
 * Ask Informed live panel (Phase 1f).
 *
 * Replaces the 1c static shell when `academy_ask_informed_enabled=true`.
 * All learner-visible states:
 *   - `grounded`          → answer text + structured citations
 *   - `unavailable`       → "insufficient approved material" state
 *   - `refused`           → safe refusal (assessment/answer-key path)
 *   - `provider_error`    → retryable transient error
 *   - `rate_limited`      → usage-limit state (retry-after)
 *   - `assessment_block`  → server-side 409 assessment_in_progress
 *
 * NEVER uses `dangerouslySetInnerHTML`. Every element has a
 * `data-testid`. Zero fake message auto-population — the panel starts
 * empty and only shows what the server returned.
 *
 * Owner access (Phase 1f owner-preview activation): when
 * `ownerAccess=true` is passed by the founder-only /academy/ask
 * wrapper AND the current user's `team_role === 'owner'`, the
 * client-side feature-flag early-return is bypassed so the founder
 * can see the real panel UI even while the flag stays False for
 * everyone else. Every server-side gate remains fully enforced;
 * this only affects the panel's own client-side render check.
 */
export default function AskInformedPanel({ programId, moduleId, lessonId, className = "", ownerAccess = false }) {
  const { isEnabled } = useFeatureFlags();
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ kind: "idle" }); // kind: idle | error | rate_limited | assessment | provider_error
  const [errorDetail, setErrorDetail] = useState(null);
  const inputRef = useRef(null);

  const flagOn = isEnabled("academy_ask_informed_enabled");
  const isOwnerAccess = ownerAccess && user?.team_role === "owner";
  const visible = flagOn || isOwnerAccess;

  const startConversation = useCallback(async () => {
    if (!programId) return;
    try {
      // Resume-aware: prefer the most-recent existing active
      // conversation for this user + program so the owner can revisit
      // real prior questions across page loads. Only resumes
      // conversations that actually contain messages (so empty auto-
      // created shells never surface as "the current conversation").
      // Never auto-populates fake data — messages loaded here come
      // straight from the server.
      const list = await api.get("/academy/ask/conversations", { params: { limit: 20 } });
      const items = Array.isArray(list.data?.items) ? list.data.items : [];
      const candidates = items.filter((c) => c.program_id === programId);
      for (const cand of candidates) {
        const detail = await api.get(`/academy/ask/conversations/${cand.id}`);
        const serverMsgs = Array.isArray(detail.data?.messages) ? detail.data.messages : [];
        if (serverMsgs.length > 0) {
          setConversationId(cand.id);
          setMessages(
            serverMsgs.map((m) => ({
              id: m.id,
              role: m.role,
              text: m.text,
              citations: m.citations || [],
              grounded: m.answer_provenance === "grounded",
              refused: m.answer_provenance === "refused",
              unavailable: m.answer_provenance === "unavailable",
            }))
          );
          return;
        }
      }
      // No existing conversation has messages yet — defer conversation
      // creation to first-submit so we never persist empty shells.
    } catch (err) {
      setState({ kind: "error" });
      setErrorDetail(formatApiError(err));
    }
  }, [programId]);

  useEffect(() => {
    if (visible && !conversationId && programId) startConversation();
  }, [visible, conversationId, programId, startConversation]);

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    setState({ kind: "idle" });
    setErrorDetail(null);
    // Lazy-create the conversation on first-submit so we never
    // persist empty conversation shells.
    let cid = conversationId;
    if (!cid) {
      if (!programId) { setBusy(false); return; }
      try {
        const r = await api.post("/academy/ask/conversations", {
          program_id: programId, module_id: moduleId || null, lesson_id: lessonId || null,
          title: "Ask Informed",
        });
        cid = r.data.id;
        setConversationId(cid);
      } catch (err) {
        setBusy(false);
        setState({ kind: "error" });
        setErrorDetail(formatApiError(err));
        return;
      }
    }
    const localUserMsg = { id: `local-${Date.now()}`, role: "user", text, citations: [] };
    setMessages((prev) => [...prev, localUserMsg]);
    setDraft("");
    try {
      const r = await api.post(
        `/academy/ask/conversations/${cid}/messages`,
        { text },
      );
      const asst = {
        id: r.data.message_id,
        role: "assistant",
        text: r.data.answer_text,
        citations: r.data.citations || [],
        grounded: r.data.grounded,
        refused: r.data.refused,
        unavailable: r.data.unavailable,
        suggested_lesson_id: r.data.suggested_lesson_id,
      };
      setMessages((prev) => [...prev, asst]);
    } catch (err) {
      const st = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (st === 409 && code === "assessment_in_progress") {
        setState({ kind: "assessment" });
      } else if (st === 429) {
        setState({
          kind: "rate_limited",
          retry_after_seconds: err?.response?.data?.detail?.retry_after_seconds,
          cap: err?.response?.data?.detail?.cap,
          code,
        });
      } else if (st === 503) {
        setState({ kind: "provider_error" });
      } else {
        setState({ kind: "error" });
        setErrorDetail(formatApiError(err));
      }
    } finally {
      setBusy(false);
      // Focus back to input for accessibility.
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [draft, conversationId, programId, moduleId, lessonId]);

  const retry = () => { setState({ kind: "idle" }); setDraft(messages[messages.length - 1]?.text || ""); };

  if (!visible) return null;

  return (
    <aside
      data-testid="academy-ask-informed-panel"
      data-program-id={programId || ""}
      data-module-id={moduleId || ""}
      data-lesson-id={lessonId || ""}
      className={`rounded-xl border border-v2-border bg-v2-surface p-5 ${className}`.trim()}
      aria-label="Ask Informed coach"
    >
      <header className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: "var(--v2-accent-soft)", color: "var(--v2-accent)" }}>
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-v2-text" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            Ask Informed
          </p>
          <p className="text-[11px] text-v2-text-muted">AI coach — grounded on your Academy material</p>
        </div>
      </header>

      {/* Conversation body */}
      <div data-testid="ask-conversation" className="space-y-3 max-h-96 overflow-y-auto pr-1" role="log" aria-live="polite">
        {messages.length === 0 && (
          <p data-testid="ask-empty-state" className="text-xs text-v2-text-muted">
            Ask a question about this lesson or program. Answers cite the exact Academy material they draw from.
          </p>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}
      </div>

      {/* State banners */}
      {state.kind === "assessment" && (
        <div data-testid="ask-assessment-block" className="mt-3 flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/5 text-amber-300 text-xs">
          <ShieldAlert className="w-4 h-4 mt-0.5" />
          <div>
            Ask Informed is paused while an assessment is in progress. Submit or exit the assessment to continue.
          </div>
        </div>
      )}
      {state.kind === "rate_limited" && (
        <div data-testid="ask-rate-limited" className="mt-3 flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/5 text-amber-300 text-xs">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            {state.code === "daily_cap_exceeded"
              ? `Daily coaching limit reached (${state.cap || 60}). Please try again tomorrow.`
              : `You're sending questions faster than the coach can handle. Try again in ~${state.retry_after_seconds || 30}s.`}
          </div>
        </div>
      )}
      {state.kind === "provider_error" && (
        <div data-testid="ask-provider-error" className="mt-3 flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/5 text-red-300 text-xs">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div className="flex-1">
            The coach service is temporarily unavailable. Your question wasn't lost.
          </div>
          <button data-testid="ask-retry" onClick={retry} className="inline-flex items-center gap-1 text-red-300 hover:text-white">
            <RefreshCcw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}
      {state.kind === "error" && (
        <div data-testid="ask-generic-error" className="mt-3 p-3 rounded-md border border-red-500/40 bg-red-500/5 text-red-300 text-xs">
          {errorDetail || "Something went wrong. Please try again."}
        </div>
      )}

      {/* Composer */}
      <div className="mt-4 flex items-start gap-2">
        <label htmlFor="ask-input" className="sr-only">Your question</label>
        <textarea
          id="ask-input"
          ref={inputRef}
          data-testid="ask-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
          disabled={busy || state.kind === "assessment"}
          rows={2}
          placeholder="Ask about this lesson…"
          className="flex-1 rounded-md border border-v2-border bg-v2-bg px-3 py-2 text-sm text-v2-text disabled:opacity-60"
        />
        <button
          data-testid="ask-submit"
          onClick={submit}
          disabled={busy || !draft.trim() || state.kind === "assessment"}
          aria-label="Send question"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#A78BFA] text-black text-sm font-medium hover:bg-[#9D7AEA] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <p className="mt-2 text-[10px] text-v2-text-muted">
        AI-generated. Not a licensed professional. Always review the cited lesson.
      </p>
    </aside>
  );
}


function MessageBubble({ msg }) {
  if (msg.role === "user") {
    return (
      <div data-testid={`ask-msg-user-${msg.id}`} className="flex justify-end">
        <div className="max-w-[80%] rounded-lg px-3 py-2 text-sm bg-[#A78BFA] text-black">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`ask-msg-assistant-${msg.id}`} className="flex justify-start">
      <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-v2-bg border border-v2-border">
        {msg.unavailable ? (
          <div data-testid="ask-unavailable" className="text-v2-text-muted italic">
            {msg.text}
          </div>
        ) : msg.refused ? (
          <div data-testid="ask-refused" className="text-v2-text-muted italic">
            {msg.text}
          </div>
        ) : (
          <div data-testid="ask-answer" className="text-v2-text whitespace-pre-wrap">
            {msg.text}
          </div>
        )}
        <CitationRefs citations={msg.citations || []} />
      </div>
    </div>
  );
}
